import type { PageProps } from "next";
import { useMemo } from "react";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";
import { withRelay } from "relay-nextjs";
import type { RelayComponent } from "relay-nextjs";

import { AdSlot } from "~/components/advertisement/ad-slots/ad-slot";
import {
  TargetingHomePageTakeOverType,
  TargetingPaidType,
} from "~/components/advertisement/ad-slots/types";
import { useAppBarAdSlot } from "~/components/app-bar/hooks";
import { AppFooter } from "~/components/app-footer";
import { FallbackLoading } from "~/components/fallback-loading";
import { TopicSeo } from "~/components/topic/topic-seo";
import { data as appData } from "~/data";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import { parseQueryString } from "~/lib/utils";
import type { topicPageQuery } from "~/queries/__generated__/topicPageQuery.graphql";
import type { topicTopicPageTopic$key } from "~/queries/__generated__/topicTopicPageTopic.graphql";

import {
  AdSlotWrapper,
  Container,
  Content,
  Divider,
  Header,
  PrimaryTopicContainer,
  SecondaryContainer,
} from "./styles";

const query = graphql`
  query topicPageQuery(
    $entityId: String!
    $superTopicQueueName: String!
    $sponsorArticleQueueName: String!
    $applicationIds: [String]
    $opinionAndColumnArticleTypeIds: [String]
    $after: String
    $count: Int = 30
  ) {
    topic(filter: { entityId: $entityId }) {
      entityId
      types
      advertZone
      sponsor {
        __typename
      }
      ...topicContentListTopic
        @arguments(applicationIds: $applicationIds, after: $after, count: $count)
      ...topicTopicPageTopic
        @arguments(applicationIds: $applicationIds, after: $after, count: $count)
      ...topicContentListOpinion
        @arguments(
          applicationIds: $applicationIds
          opinionAndColumnArticleTypeIds: $opinionAndColumnArticleTypeIds
        )
      ...topicHeaderTopic
      ...topicSeoTopic
    }
    ...topicContentListTopicSuperTopicQueueQuery
      @arguments(entityId: $entityId, superTopicQueueName: $superTopicQueueName)
    ...topicSponsorArticlesQueueQuery @arguments(sponsorArticleQueueName: $sponsorArticleQueueName)
  }
`;

const TopicPage: RelayComponent<unknown, topicPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const isOpinionType = ((data.topic?.types ?? []) as string[]).includes("opinion");
  const hasSponsor = !!data.topic.sponsor;
  const hideSecondaryContainer = isOpinionType || hasSponsor;

  const { data: contentList, hasNext: hasNextPage } = usePaginationFragment<
    topicPageQuery,
    topicTopicPageTopic$key
  >(
    graphql`
      fragment topicTopicPageTopic on Topic
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 30 }
        applicationIds: { type: "[String]", defaultValue: [] }
      )
      @refetchable(queryName: "topicTopicPagePaginationQuery") {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "topicTopicPage__author_contents") {
          edges {
            __typename
          }
        }
      }
    `,
    data.topic,
  );

  useAppBarAdSlot({
    desktop: null,
    mobile: {
      adUnit: "m_banner3",
      targeting: {
        hpto: TargetingHomePageTakeOverType.Top,
        paid: TargetingPaidType.Free,
        sctid: data.topic.entityId,
      },
      zone: data.topic.advertZone,
    },
  });

  const isShowFooter = useMemo(
    () =>
      // this is to prevent eslint throw warning relay/unused-fields
      !contentList.contents?.edges || !hasNextPage,
    [contentList.contents?.edges, hasNextPage],
  );

  return (
    <>
      <TopicSeo reference={data.topic} />
      <Container>
        <Header reference={data.topic} />
        <Content>
          {/** Remarks: component order is for preventing layout shift */}
          <AdSlotWrapper>
            <AdSlot
              adUnit="d_lrec2"
              sizes={[[300, 250], "fluid"]}
              targeting={{
                paid: TargetingPaidType.Free,
                sctid: data.topic.entityId,
              }}
              zone={data.topic.advertZone}
            />
          </AdSlotWrapper>
          {!hideSecondaryContainer && <SecondaryContainer reference={data.topic} />}
          <Divider />
          <PrimaryTopicContainer
            showColumnAndOpinionArticles={isOpinionType}
            sponsorArticleReference={data}
            superTopicReference={data}
            topicReference={data.topic}
          />
        </Content>
      </Container>
      {isShowFooter && <AppFooter />}
    </>
  );
};

TopicPage.displayName = "TopicPage";

const pageProps: PageProps = {
  appBarConfiguration: {
    hasDesktopAd: false,
    hasMobileAd: true,
  },
  contentConfiguration: {
    responsivePadding: {
      desktopUp: 0,
      tabletUp: 0,
    },
  },
};

export default withRelay(TopicPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    const entityId = parseQueryString(context.query.entityId) ?? "";
    return {
      applicationIds: [appData.application.scmp.entityUuid],
      entityId,
      opinionAndColumnArticleTypeIds: [
        appData.articleTypeEntityUuids.Column,
        appData.articleTypeEntityUuids.Opinion,
      ],
      sponsorArticleQueueName: `topic_sponsor_articles_${entityId}`,
      superTopicQueueName: `topic_super_topic_${entityId}`,
    };
  },
});
